import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  getFirmMembers,
  revokeAdvisor,
  transferBook,
  type FirmMemberRow,
} from '../lib/advisorApi';
import './advisor.css';

/**
 * The firm's own people — and what happens when one of them leaves.
 *
 * This screen exists for a day that is always awkward and sometimes worse: an
 * advisor has left, possibly not amicably, and their clients are sitting in an
 * account nobody is watching. The firm needs to move that book and end that
 * access themselves, at the moment they decide to, without emailing us.
 *
 * Two deliberate pieces of friction, because both of these are irreversible in
 * the ways that matter:
 *
 *  - Handing over a book is a real decision about real families, so it opens a
 *    panel that names the receiving advisor and says how many clients move.
 *  - Ending someone's access asks for their name to be typed. Not ceremony:
 *    this is the one button on the screen that cannot be undone with another
 *    click, and the row you meant is one line above the row you clicked.
 *
 * What this screen will not do is let a firm strand its own clients or lock
 * itself out — the server refuses both, and the copy here explains why rather
 * than presenting a dead button.
 */
export function TeamScreen() {
  const { api } = useAuth();
  const [members, setMembers] = useState<FirmMemberRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Which row has a panel open, and which panel.
  const [transferFor, setTransferFor] = useState<FirmMemberRow | null>(null);
  const [revokeFor, setRevokeFor] = useState<FirmMemberRow | null>(null);
  const [toAdvisorId, setToAdvisorId] = useState('');
  const [reason, setReason] = useState('');
  const [alsoRevoke, setAlsoRevoke] = useState(true);
  const [typedName, setTypedName] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await getFirmMembers(api);
      setMembers(res.members);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We could not load your team.');
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  function closePanels(): void {
    setTransferFor(null);
    setRevokeFor(null);
    setToAdvisorId('');
    setReason('');
    setTypedName('');
    setAlsoRevoke(true);
  }

  async function doTransfer(): Promise<void> {
    if (transferFor === null || toAdvisorId === '') return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await transferBook(api, transferFor.userId, {
        toAdvisorId, reason: reason.trim() === '' ? undefined : reason.trim(),
        revokeDeparting: alsoRevoke,
      });
      const to = members?.find((m) => m.userId === toAdvisorId);
      setNotice(
        `${res.clientsMoved} ${res.clientsMoved === 1 ? 'client' : 'clients'} moved to `
        + `${to?.fullName ?? 'the receiving advisor'}`
        + (res.invitesMoved > 0 ? `, along with ${res.invitesMoved} unaccepted invitation${res.invitesMoved === 1 ? '' : 's'}` : '')
        + (res.departingRevoked ? '. Their access has ended.' : '.'),
      );
      closePanels();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The handover did not complete.');
    } finally { setBusy(false); }
  }

  async function doRevoke(): Promise<void> {
    if (revokeFor === null) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await revokeAdvisor(api, revokeFor.userId, reason.trim() === '' ? undefined : reason.trim());
      setNotice(
        `${revokeFor.fullName ?? revokeFor.email} is signed out everywhere`
        + (res.passkeysRevoked > 0 ? ` and ${res.passkeysRevoked} passkey${res.passkeysRevoked === 1 ? '' : 's'} removed` : '')
        + '. They no longer have access to this firm.',
      );
      closePanels();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We could not end their access.');
    } finally { setBusy(false); }
  }

  const others = (members ?? []).filter((m) => m.userId !== transferFor?.userId);
  const nameToType = revokeFor?.fullName ?? revokeFor?.email ?? '';
  const nameMatches = typedName.trim().toLowerCase() === nameToType.trim().toLowerCase();

  return (
    <section>
      <header className="page__head">
        <p className="la-label">Your firm</p>
        <h1 className="page__title">Advisors</h1>
        <p className="page__sub">
          Everyone here signs in with their own account, so the audit log always shows which
          advisor did what. When someone leaves, move their clients first, then end their access.
        </p>
      </header>

      {notice !== null && <p className="page__notice" role="status">{notice}</p>}
      {error !== null && <p className="auth__alert" role="alert">{error}</p>}

      {members === null ? (
        <p className="page__sub">Loading…</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Advisor</th>
              <th>Role</th>
              <th>Clients</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.userId}>
                <td>
                  <strong>{m.fullName ?? '—'}</strong>
                  {m.isYou && <span className="pill pill--quiet">you</span>}
                  <div className="table__meta">{m.email}</div>
                </td>
                <td>{m.isPrimary ? 'Administrator' : 'Advisor'}</td>
                <td>{m.clients}</td>
                <td className="table__actions">
                  {m.clients > 0 && (
                    <button
                      type="button"
                      className="la-btn-quiet"
                      onClick={() => { closePanels(); setTransferFor(m); }}
                    >
                      Hand over clients
                    </button>
                  )}
                  {!m.isYou && (
                    <button
                      type="button"
                      className="la-btn-quiet la-btn-quiet--danger"
                      onClick={() => { closePanels(); setRevokeFor(m); }}
                    >
                      End access
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {transferFor !== null && (
        <div className="panel la-card">
          <h2 className="panel__title">
            Hand over {transferFor.fullName ?? transferFor.email}&rsquo;s clients
          </h2>
          <p className="page__sub">
            {transferFor.clients} {transferFor.clients === 1 ? 'client moves' : 'clients move'} to
            another advisor, along with any invitations they sent that nobody has accepted yet.
            Nothing your clients see will change, and no sealed message is touched.
          </p>

          <label className="auth__label" htmlFor="to">Who takes them</label>
          <select
            id="to"
            className="auth__input"
            value={toAdvisorId}
            onChange={(e) => setToAdvisorId(e.target.value)}
          >
            <option value="">Choose an advisor…</option>
            {others.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.fullName ?? m.email}{m.clients > 0 ? ` — ${m.clients} already` : ''}
              </option>
            ))}
          </select>

          <label className="auth__label" htmlFor="reason">Reason (kept in the audit log)</label>
          <input
            id="reason"
            className="auth__input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Left the firm"
          />

          <label className="checkbox">
            <input
              type="checkbox"
              checked={alsoRevoke}
              onChange={(e) => setAlsoRevoke(e.target.checked)}
            />
            <span>End their access once the clients have moved</span>
          </label>

          <div className="panel__actions">
            <button type="button" className="la-btn-quiet" onClick={closePanels} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="la-btn-primary"
              disabled={busy || toAdvisorId === ''}
              onClick={() => void doTransfer()}
            >
              {busy ? 'Moving…' : 'Hand over'}
            </button>
          </div>
        </div>
      )}

      {revokeFor !== null && (
        <div className="panel la-card panel--danger">
          <h2 className="panel__title">End {nameToType}&rsquo;s access</h2>
          <p className="page__sub">
            They are signed out of every device immediately, their password and passkeys stop
            working, and they are removed from this firm. Their client records and everything
            they did stay exactly as they are.
          </p>
          {revokeFor.clients > 0 && (
            <p className="auth__alert" role="alert">
              They still have {revokeFor.clients} {revokeFor.clients === 1 ? 'client' : 'clients'}.
              Hand those over first, or nobody will be looking after them.
            </p>
          )}

          <label className="auth__label" htmlFor="confirm">
            Type their name to confirm: <strong>{nameToType}</strong>
          </label>
          <input
            id="confirm"
            className="auth__input"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            autoComplete="off"
          />

          <label className="auth__label" htmlFor="revoke-reason">Reason (kept in the audit log)</label>
          <input
            id="revoke-reason"
            className="auth__input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Left the firm"
          />

          <div className="panel__actions">
            <button type="button" className="la-btn-quiet" onClick={closePanels} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="la-btn-primary la-btn-primary--danger"
              disabled={busy || !nameMatches || revokeFor.clients > 0}
              onClick={() => void doRevoke()}
            >
              {busy ? 'Ending…' : 'End access'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
